import { getSecureAtomicTime } from '../services/firebase';

const LANGUAGE_LOCALE_MAP = {
  'English': 'en-US',
  'Arabic': 'ar-SA',
  'Bahasa Indonesia': 'id-ID',
  'Brazilian Portuguese': 'pt-BR',
  'Bangla': 'bn-BD',
  'Spanish': 'es-ES',
  'French': 'fr-FR',
  'Italian': 'it-IT',
  'German': 'de-DE',
  'Vietnamese': 'vi-VN'
};

const COUNTRY_TIMEZONE_MAP = {
  "Afghanistan": "Asia/Kabul", "Albania": "Europe/Tirane", "Algeria": "Africa/Algiers", "Andorra": "Europe/Andorra", "Angola": "Africa/Luanda", "Argentina": "America/Argentina/Buenos_Aires", "Australia": "Australia/Sydney", "Austria": "Europe/Vienna", "Bahamas": "America/Nassau", "Bahrain": "Asia/Bahrain", "Bangladesh": "Asia/Dhaka", "Barbados": "America/Barbados", "Belarus": "Europe/Minsk", "Belgium": "Europe/Brussels", "Belize": "America/Belize", "Brazil": "America/Sao_Paulo", "Bulgaria": "Europe/Sofia", "Cambodia": "Asia/Phnom_Penh", "Cameroon": "Africa/Douala", "Canada": "America/Toronto", "Chile": "America/Santiago", "China": "Asia/Shanghai", "Colombia": "America/Bogota", "Costa Rica": "America/Costa_Rica", "Croatia": "Europe/Zagreb", "Cuba": "America/Havana", "Cyprus": "Asia/Nicosia", "Czechia (Czech Republic)": "Europe/Prague", "Denmark": "Europe/Copenhagen", "Dominica": "America/Dominica", "Dominican Republic": "America/Santo_Domingo", "Ecuador": "America/Guayaquil", "Egypt": "Africa/Cairo", "El Salvador": "America/El_Salvador", "Estonia": "Europe/Tallinn", "Ethiopia": "Africa/Addis_Ababa", "Fiji": "Pacific/Fiji", "Finland": "Europe/Helsinki", "France": "Europe/Paris", "Germany": "Europe/Berlin", "Ghana": "Africa/Accra", "Greece": "Europe/Athens", "Guatemala": "America/Guatemala", "Honduras": "America/Tegucigalpa", "Hungary": "Europe/Budapest", "Iceland": "Atlantic/Reykjavik", "India": "Asia/Kolkata", "Indonesia": "Asia/Jakarta", "Iran": "Asia/Tehran", "Iraq": "Asia/Baghdad", "Ireland": "Europe/Dublin", "Israel": "Asia/Jerusalem", "Italy": "Europe/Rome", "Jamaica": "America/Jamaica", "Japan": "Asia/Tokyo", "Jordan": "Asia/Amman", "Kazakhstan": "Asia/Almaty", "Kenya": "Africa/Nairobi", "Kuwait": "Asia/Kuwait", "Lebanon": "Asia/Beirut", "Libya": "Africa/Tripoli", "Lithuania": "Europe/Vilnius", "Luxembourg": "Europe/Luxembourg", "Madagascar": "Africa/Antananarivo", "Malaysia": "Asia/Kuala_Lumpur", "Maldives": "Indian/Maldives", "Malta": "Europe/Malta", "Mexico": "America/Mexico_City", "Micronesia": "Pacific/Pohnpei", "Monaco": "Europe/Monaco", "Mongolia": "Asia/Ulaanbaatar", "Morocco": "Africa/Casablanca", "Myanmar (formerly Burma)": "Asia/Yangon", "Nepal": "Asia/Kathmandu", "Netherlands": "Europe/Amsterdam", "New Zealand": "Pacific/Auckland", "Nicaragua": "America/Managua", "Nigeria": "Africa/Lagos", "North Korea": "Asia/Pyongyang", "Norway": "Europe/Oslo", "Oman": "Asia/Muscat", "Pakistan": "Asia/Karachi", "Palestine State": "Asia/Gaza", "Panama": "America/Panama", "Paraguay": "America/Asuncion", "Peru": "America/Lima", "Philippines": "Asia/Manila", "Poland": "Europe/Warsaw", "Portugal": "Europe/Lisbon", "Qatar": "Asia/Qatar", "Romania": "Europe/Bucharest", "Russia": "Europe/Moscow", "Saudi Arabia": "Asia/Riyadh", "Senegal": "Africa/Dakar", "Serbia": "Europe/Belgrade", "Singapore": "Asia/Singapore", "Slovakia": "Europe/Bratislava", "Slovenia": "Europe/Ljubljana", "South Africa": "Africa/Johannesburg", "South Korea": "Asia/Seoul", "Spain": "Europe/Madrid", "Sri Lanka": "Asia/Colombo", "Sudan": "Africa/Khartoum", "Sweden": "Europe/Stockholm", "Switzerland": "Europe/Zurich", "Syria": "Asia/Damascus", "Taiwan": "Asia/Taipei", "Tanzania": "Africa/Dar_es_Salaam", "Thailand": "Asia/Bangkok", "Tunisia": "Africa/Tunis", "Turkey": "Europe/Istanbul", "Uganda": "Africa/Kampala", "Ukraine": "Europe/Kyiv", "United Arab Emirates": "Asia/Dubai", "United Kingdom": "Europe/London", "United States of America": "America/New_York", "Uruguay": "America/Montevideo", "Uzbekistan": "Asia/Tashkent", "Venezuela": "America/Caracas", "Vietnam": "Asia/Ho_Chi_Minh", "Yemen": "Asia/Aden", "Zambia": "Africa/Lusaka", "Zimbabwe": "Africa/Harare"
};

/**
 * Gets the current atomic time from Firebase.
 * Replaces the old string-based IST fetcher.
 * @returns {number} Unix epoch timestamp in milliseconds
 */
export const getCurrentISTTime = async () => {
  return getSecureAtomicTime();
};

/**
 * Legacy formatter (now just an alias for integers to ensure DB gets pure epochs)
 */
export const formatDateForDB = (date) => {
  return date.getTime();
};

/**
 * Reusable engine to parse both legacy strings and modern integers
 * @param {string|number} dbTime - Time from database
 * @returns {Date} Active Javascript Date object
 */
export const parseDatabaseTime = (dbTime) => {
  if (!dbTime) return new Date();

  // Modern: integer Unix epoch
  if (typeof dbTime === 'number') {
    return new Date(dbTime);
  }

  // Legacy: string DD:MM:YYYY HH:MM:SS
  try {
    if (typeof dbTime === 'string' && dbTime.includes(':') && dbTime.split(' ').length === 2) {
      const parts = dbTime.split(' ');
      const dateParts = parts[0].split(':');
      const timeParts = parts[1].split(':');

      if (dateParts.length === 3 && timeParts.length >= 2) {
        const day = dateParts[0].padStart(2, '0');
        const month = dateParts[1].padStart(2, '0');
        const year = dateParts[2];
        const h = timeParts[0].padStart(2, '0');
        const m = timeParts[1].padStart(2, '0');
        const s = (timeParts[2] || '00').padStart(2, '0');

        // Construct an explicit ISO 8601 string asserting this is Asia/Kolkata (+05:30)
        const isoString = `${year}-${month}-${day}T${h}:${m}:${s}+05:30`;
        const offsetDate = new Date(isoString);
        
        if (!isNaN(offsetDate.getTime())) {
          return offsetDate;
        }
      }
    }
  } catch (error) {
    console.error("Error parsing legacy date string:", error);
  }

  // Ultimate fallback
  return new Date(dbTime);
};

/**
 * Universal Display Utility
 * Renders any database time into the user's explicit timezone and language
 * @param {string|number} dbTime - Time from database 
 * @param {string} [country] - User's selected country
 * @param {string} [language] - User's selected language
 * @param {string} [userTimezone] - User's explicit timezone
 * @returns {string} Formatted display string
 */
export const displayTimeFromDB = (dbTime, country = null, language = null, userTimezone = null) => {
  if (!dbTime) return 'N/A';

  try {
    const dateObj = parseDatabaseTime(dbTime);
    
    let tz = userTimezone && userTimezone !== '' ? userTimezone : Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!userTimezone && country && COUNTRY_TIMEZONE_MAP[country]) {
      tz = COUNTRY_TIMEZONE_MAP[country];
    }

    let locale = 'en-US';
    if (language && LANGUAGE_LOCALE_MAP[language]) {
      locale = LANGUAGE_LOCALE_MAP[language];
    }

    return new Intl.DateTimeFormat(locale, {
      timeZone: tz,
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(dateObj);

  } catch (error) {
    return String(dbTime);
  }
};
